import {
  FormControl,
  HStack,
  SearchInput,
  Stack,
} from '@metservicedev/met-ui';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { ApiVolcano, UNKNOWN_VOLCANO_NAME, UNKNOWN_VOLCANO_NUMBER } from '@vona/shared';
import { HeightUnitsWithoutKM } from '@vona/shared/Height';

import { createStyles } from '../tss';
import { FormVona } from '../types/FormVona';
import { FormSelect } from './FormSelect';
import { FormTextInput } from './FormTextInput';
import { InfoToolTip } from './InfoToolTip';
import { ValidationContainer } from './ValidationContainer';
import { ValidationMessage } from './ValidationMessage';

const volcanoKey = (volcano: Pick<ApiVolcano, 'number' | 'name'>) => `${volcano.number} - ${volcano.name}`;
const UNKNOWN_KEY = volcanoKey({ number: UNKNOWN_VOLCANO_NUMBER, name: UNKNOWN_VOLCANO_NAME });

const useStyles = createStyles(({ theme }) => ({
  coordsAndElevation: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(1),
    flex: 1,
    [theme.breakpoints.below('sm')]: {
      flexDirection: 'column',
      justifyContent: 'flex-start',
    },
  },
  coordsValue: {
    textTransform: 'uppercase',
  },
  heightForm: {
    minWidth: 210,
    maxWidth: 210,
    [theme.breakpoints.below('sm')]: {
      maxWidth: 'unset',
    },
  },
  heightValue: {
    borderBottomRightRadius: 0,
    borderTopRightRadius: 0,
    textTransform: 'uppercase',
  },
  heightUnit: {
    '& button': {
      borderBottomLeftRadius: 0,
      borderTopLeftRadius: 0,
    },
  },
  heightUnitForm: {
    marginLeft: -1,
    minWidth: 96,
    maxWidth: 96,
    '& :focus, & :hover, & :is([open],[data-open],[data-state=open])': {
      zIndex: 1,
    },
  },
  searchInput: {
    '& [data-part="clear-trigger"]': {
      display: 'none',
    },
  },
}));

type Props = {
  urlVolcanoNumber?: string;
  volcanoes: ApiVolcano[];
  onSelectVolcano: (volcanoNumber: string) => void;
  onSelectUnknown: () => void;
};

export const VolcanoInputs: React.FC<Props> = ({
  urlVolcanoNumber,
  volcanoes,
  onSelectVolcano,
  onSelectUnknown,
}) => {
  const { classes } = useStyles();
  const form = useFormContext<FormVona>();
  const isSummitHeightUnknown = form.watch('volcano.summitHeightAmsl.value') === 'UNKNOWN';

  return (
    <>
      <ValidationContainer hstack>
        <Controller
          control={form.control}
          name="volcano.name"
          rules={{ deps: ['volcano.number'] }}
          render={({ field: { onBlur, ref } }) => (
            <FormControl
              disabled={urlVolcanoNumber !== undefined}
              invalid={!!form.formState.errors.volcano?.name}
            >
              <SearchInput
                className={classes.searchInput}
                data-testid="select-volcano-name"
                disabled={urlVolcanoNumber !== undefined}
                items={[
                  ...volcanoes.map((volcano) => ({
                    label: `${volcano.name} (${volcano.number})`,
                    value: volcanoKey(volcano),
                  })),
                  { label: UNKNOWN_VOLCANO_NAME, value: UNKNOWN_KEY },
                ]}
                ref={ref}
                label="Volcano"
                limit={20}
                onValueChange={(details) => {
                  if (details.value[0] === UNKNOWN_KEY) {
                    onSelectUnknown();
                    return;
                  }
                  const volcano = volcanoes.find((x) => volcanoKey(x) === details.value[0]);
                  // istanbul ignore else -- @preserve
                  if (volcano) {
                    onSelectVolcano(volcano.number);
                  }
                }}
                onBlur={onBlur}
                selectionBehavior="replace"
              />
              <ValidationMessage message={form.formState.errors.volcano?.name?.message} />
            </FormControl>
          )}
        />
        <input
          data-testid="input-volcano-name"
          type="hidden"
          {...form.register('volcano.name')}
        />
        <input
          data-testid="input-volcano-number"
          type="hidden"
          {...form.register('volcano.number', { deps: ['volcano.name'] })}
        />
        <InfoToolTip
          data-testid="volcano-name-tooltip"
          tooltipContent="
Select a volcano from the list or type the name of the volcano; the number will
be populated automatically when a volcano is selected. If you cannot find the
volcano in the list, select 'Unknown' and enter the Smithsonian Institution
volcano number manually.
"
        />
      </ValidationContainer>
      <ValidationContainer hstack>
        <div className={classes.coordsAndElevation}>
          <FormTextInput
            textInputProps={{ className: classes.coordsValue }}
            data-testid="input-volcano-coordinates"
            fieldPath="volcano.coordinates.0"
            formControlProps={{ width: 'full' }}
            label="Coordinates"
          />
          <HStack gap={0}>
            <FormTextInput
              data-testid="input-summit-elevation-height"
              fieldPath="volcano.summitHeightAmsl.value"
              formControlProps={{ className: classes.heightForm }}
              textInputProps={{ className: classes.heightValue }}
              label="Source Elevation (AMSL)"
            />
            <FormSelect
              data-testid="select-summit-elevation-unit"
              disabled={isSummitHeightUnknown}
              dependants={['volcano.summitHeightAmsl.value']}
              formControlProps={{ className: classes.heightUnitForm }}
              selectProps={{ className: classes.heightUnit }}
              fieldPath="volcano.summitHeightAmsl.unit"
              items={HeightUnitsWithoutKM.map((unit) => ({ label: unit, value: unit }))}
              label="Unit"
            />
          </HStack>
        </div>
        <InfoToolTip
          tooltipContent={(
            <Stack gap={1}>
              <span>Coordinates of the volcano in NOTAM format (e.g. N1234 E12345).</span>
              <span>Height of the volcano summit above mean sea level.</span>
              <span>The units of the volcano summit height.</span>
            </Stack>
          )}
          data-testid="tooltip-cloud-height"
        />
      </ValidationContainer>
      <FormTextInput
        data-testid="input-volcano-area"
        fieldPath="volcano.area"
        label="Area"
        tooltipContent="State or region involved."
      />
    </>
  );
};
